import { createColumnHelper } from "@tanstack/react-table";
import {
  ActivityQuery,
  AuthConfig,
  Threat,
  ThreatsActivity,
  createRecordFromArray,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import {
  benchmarkColumn,
  protectedColumn,
  sourceIdColumn,
  threatNameSmallColumn,
} from "@/components/table/columns";
import { createQuery } from "@/lib/create-query";
import { isPresent } from "@/lib/validators";
import QuickViewTable from "./quickview-table";

interface QuickviewThreats {
  threatId: string;
  source_id?: string;
  source?: string;
  threat: string;
  protected?: number;
  benchmark?: number;
}

const columnHelper = createColumnHelper<QuickviewThreats>();

const columns = [
  columnHelper.accessor("source_id", sourceIdColumn),
  columnHelper.accessor("threat", threatNameSmallColumn),
  columnHelper.accessor("protected", protectedColumn),
  columnHelper.accessor("benchmark", benchmarkColumn),
];

function getThreatsData(threats: Threat[], activity: ThreatsActivity[]) {
  const activityMap = createRecordFromArray(activity, "id");

  return threats
    .filter((threat) => isPresent(activityMap[threat.id]?.protected))
    .map((threat) => {
      return {
        threatId: threat.id,
        source_id: threat.source_id
          ? threat.source_id.toUpperCase()
          : undefined,
        source: threat.source ?? undefined,
        threat: threat.name,
        protected: activityMap[threat.id]?.protected ?? undefined,
        benchmark: activityMap[threat.id]?.social ?? undefined,
      };
    });
}

const threatsQuery = (authConfig: AuthConfig, query: ActivityQuery) =>
  createQuery({
    queries: [
      queries.detect.threats(authConfig),
      queries.detect.activity.threats(authConfig, query),
    ],
    queryFn: getThreatsData,
  });

export function ThreatsTable({ query }: { query: ActivityQuery }) {
  const authConfig = useAuthConfig();
  const queryInfo = threatsQuery(authConfig, query).useQuery();

  return (
    <QuickViewTable
      name="quickview-threats"
      data={queryInfo.data ?? []}
      columns={columns}
      isLoading={queryInfo.isLoading}
    />
  );
}
